/* ── data states ────────────────────────────────────────────── */
import { useEffect, useState } from 'react';

/**
 * Three dots, waiting.
 *
 * What an assistant shows between the question and the first word of the
 * answer. Wordless on purpose: the wait is short, and a sentence that has to be
 * read and then replaced costs the reader more than it tells them.
 * `aria-label` is what a screen reader gets instead of the animation.
 */
export function TypingDots({ label = 'Thinking' }) {
  return (
    <span className="dots" role="status" aria-label={label}>
      <i /><i /><i />
    </span>
  );
}

/**
 * How fast a finished reply is written out, and the longest it may take.
 *
 * Around a brisk typing pace — fast enough to stay ahead of the reader, slow
 * enough that the sentence has a beginning. The cap is what a long answer
 * falls back to, so length costs a little time rather than all of it.
 */
const REVEAL_CPS = 45;
const REVEAL_CAP_MS = 4000;

/**
 * A reply that arrived whole, written out as if it were being composed.
 *
 * The agent sends its finished answer in one piece, and a paragraph appearing
 * between two frames reads as a page swap rather than as an answer: there is no
 * moment where the reader can tell it began. Revealing it gives the eye a place
 * to start and a rate to follow.
 *
 * This is presentation only — the message in state is complete from the first
 * frame, so nothing downstream has to wait for the animation, and a reader who
 * scrolls away mid-reveal loses nothing.
 *
 * Driven by the clock rather than by a character timer, so the reveal takes the
 * same time on a slow frame as on a fast one, and capped so a long answer is
 * never slower to read than it was to write. Reduced motion skips it outright.
 */
export function Typewriter({ text = '' }) {
  const [shown, setShown] = useState(0);

  useEffect(() => {
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (still || !text) {
      setShown(text.length);
      return undefined;
    }

    const ms = Math.min(REVEAL_CAP_MS, (text.length / REVEAL_CPS) * 1000);
    let raf;
    let start;
    const tick = (now) => {
      if (start === undefined) start = now;
      const n = Math.min(text.length, Math.round((text.length * (now - start)) / ms));
      setShown(n);
      if (n < text.length) raf = requestAnimationFrame(tick);
    };
    setShown(0);
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [text]);

  return text.slice(0, shown);
}

export function Loading({ rows = 3, label = 'Loading…' }) {
  return (
    <div className="pad" style={{ color: 'var(--ink3)', fontSize: 12.5 }}>
      {label}
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="bar" style={{ height: 10, opacity: 1 - i * 0.22 }}>
            <i style={{ width: `${70 - i * 18}%`, background: 'var(--line)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function Empty({ title, hint, action }) {
  return (
    <div className="pad" style={{ textAlign: 'center', padding: '30px 18px' }}>
      <b style={{ fontSize: 13.5 }}>{title}</b>
      {hint && <p style={{ fontSize: 12.4, color: 'var(--ink3)', marginTop: 5, lineHeight: 1.5 }}>{hint}</p>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}

/**
 * A caveat that travels with the number it qualifies.
 *
 * `info` explains how to read what is above it, `caution` says the figures are
 * not what they appear, and `limit` states what the measurement does not cover.
 * Three tones and no more: a fourth would be a decoration rather than a
 * distinction the reader has to act on.
 */
/**
 * An aside beside the figures. It carries no margin of its own, because most
 * of its callers sit it inside a card that has already spaced it; `className`
 * is for the ones that stand it between two cards.
 */
export function Note({ tone = 'info', title, children, className = '' }) {
  return (
    <div className={`note n-${tone} ${className}`.trim()}>
      {title && <b>{title}</b>}
      <div>{children}</div>
    </div>
  );
}

/** Query failure — surfaced, never swallowed, so a broken resolver is visible. */
export function QueryError({ error, what }) {
  return (
    <div className="pad" style={{ fontSize: 12.4, color: 'var(--ink2)' }}>
      <b style={{ color: 'var(--red)' }}>Couldn’t load {what}.</b>
      <p style={{ marginTop: 4, lineHeight: 1.5, color: 'var(--ink3)' }}>{error?.message}</p>
    </div>
  );
}
